// Plik: src/components/ContactClient.tsx
"use client";

import { useState, useEffect } from "react";
// Używamy aliasu @/lib/firebase dla pewności ścieżki
import { db } from "@/lib/firebase"; 
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { motion, AnimatePresence } from "framer-motion";

// --- KOMPONENTY UI ---
const GridPattern = () => (
  <div className="absolute inset-0 w-full h-full opacity-[0.03] pointer-events-none" 
       style={{ backgroundImage: 'linear-gradient(#000 1px, transparent 1px), linear-gradient(90deg, #000 1px, transparent 1px)', backgroundSize: '40px 40px' }}>
  </div>
);

const CheckIcon = () => (
  <div className="flex-shrink-0 w-5 h-5 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center mt-1">
     <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="M20 6L9 17l-5-5"/></svg>
  </div>
);

// Toast Notification
const Toast = ({ onClose }: { onClose: () => void }) => (
    <motion.div 
        initial={{ opacity: 0, y: 50, x: 20 }}
        animate={{ opacity: 1, y: 0, x: 0 }}
        exit={{ opacity: 0, y: 50, x: 20 }}
        className="fixed bottom-6 right-6 z-50 bg-white border border-green-100 shadow-2xl rounded-2xl p-4 flex items-center gap-4 max-w-sm"
    >
        <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center text-green-600 flex-shrink-0">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="M20 6L9 17l-5-5"/></svg>
        </div>
        <div>
            <h4 className="font-bold text-gray-900 text-sm">Wiadomość wysłana!</h4>
            <p className="text-xs text-gray-500">Odezwiemy się najszybciej jak to możliwe.</p>
        </div>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-900 p-1">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
        </button>
    </motion.div>
);

const SERVICES = ["Strona WWW", "Optymalizacja SEO", "Prospecting", "Pakiet Tożsamości"];
const BUDGETS = ['< 5k PLN', '5k - 15k', '15k - 50k', '50k +'];

export default function ContactClient() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    service: "", 
    budget: "",
    message: ""
  });
  
  // --- ZABEZPIECZENIE HONEYPOT ---
  const [honeypot, setHoneypot] = useState(""); 
  
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [showToast, setShowToast] = useState(false);

  useEffect(() => {
    if (showToast) {
        const timer = setTimeout(() => setShowToast(false), 5000);
        return () => clearTimeout(timer);
    }
  }, [showToast]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const setSelection = (field: 'budget' | 'service', value: string) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // --- SPRAWDZENIE HONEYPOT (ANTY-SPAM) ---
    // Jeśli pole-pułapka jest wypełnione, to znaczy, że to bot.
    // Udajemy sukces, ale nie wysyłamy nic do bazy.
    if (honeypot !== "") {
        console.log("Bot detected via honeypot.");
        setStatus("success");
        setShowToast(true);
        setFormData({ name: "", email: "", phone: "", service: "", budget: "", message: "" });
        setTimeout(() => setStatus("idle"), 3000);
        return;
    }

    setStatus("submitting");

    if (!formData.service || !formData.budget) {
        alert("Proszę wybrać usługę i budżet.");
        setStatus("idle");
        return;
    }

    try {
      await addDoc(collection(db, "leads"), {
        ...formData,
        createdAt: serverTimestamp(),
        status: "new"
      });

      setStatus("success");
      setShowToast(true);
      setFormData({ name: "", email: "", phone: "", service: "", budget: "", message: "" });
      setTimeout(() => setStatus("idle"), 3000);

    } catch (error) {
      console.error("Błąd wysyłki:", error);
      setStatus("error");
    }
  };

  return (
    <main className="bg-[#F7F7F8] w-full min-h-screen relative selection:bg-blue-200 font-sans text-gray-900">

      <AnimatePresence>
          {showToast && <Toast onClose={() => setShowToast(false)} />}
      </AnimatePresence>

      <section className="pt-32 pb-20 lg:pt-40 lg:pb-32 px-6 relative">
        <GridPattern />
        
        <div className="mx-auto max-w-[1250px] relative z-10">
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">
                
                {/* --- LEWA KOLUMNA (TEKST + DANE + PROCESS DESKTOP) --- */}
                <div className="order-1">
                    
                    {/* Sekcja: Kontakt + Porozmawiajmy */}
                    <div className="mb-10">
                        <span className="text-blue-600 font-bold tracking-widest uppercase text-xs mb-4 block">Kontakt</span>
                        <h1 className="text-4xl lg:text-6xl font-bold tracking-tight text-gray-900 mb-6">
                            Porozmawiajmy o <br/>
                            <span className="text-blue-600">Twoim projekcie.</span>
                        </h1>
                        <p className="text-lg text-gray-600 leading-relaxed">
                            Masz pomysł? A może problem ze starą stroną? Wypełnij formularz, a my przygotujemy wstępną analizę i wycenę. Bez zobowiązań.
                        </p>
                    </div>

                    {/* Dane kontaktowe (Email / Telefon) */}
                    <div className="space-y-6 mb-12">
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 rounded-xl bg-white border border-gray-200 flex items-center justify-center text-gray-600">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
                            </div>
                            <div>
                                <div className="text-xs text-gray-500 uppercase tracking-wider font-bold">Email</div>
                                <a href="mailto:webpol63@gmail.com" className="text-lg font-bold text-gray-900 hover:text-blue-600 transition">webpol63@gmail.com</a>
                            </div>
                        </div>
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 rounded-xl bg-white border border-gray-200 flex items-center justify-center text-gray-600">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
                            </div>
                            <div>
                                <div className="text-xs text-gray-500 uppercase tracking-wider font-bold">Telefon</div>
                                <a href="tel:+48123456789" className="text-lg font-bold text-gray-900 hover:text-blue-600 transition">+48 690 800 452</a>
                            </div>
                        </div>
                    </div>

                    {/* --- PROCES (DESKTOP ONLY) --- */}
                    <div className="hidden lg:block bg-white rounded-2xl p-8 border border-gray-200 shadow-sm">
                        <h3 className="font-bold text-gray-900 mb-6">Co stanie się po wysłaniu formularza?</h3>
                        <ul className="space-y-4">
                            <li className="flex gap-3 text-sm text-gray-600">
                                <CheckIcon />
                                <span>Przeanalizujemy Twoje zgłoszenie w ciągu <strong>24 godzin</strong>.</span>
                            </li>
                            <li className="flex gap-3 text-sm text-gray-600">
                                <CheckIcon />
                                <span>Przygotujemy wstępny plan działania lub umówimy krótką rozmowę (Discovery Call).</span>
                            </li>
                            <li className="flex gap-3 text-sm text-gray-600">
                                <CheckIcon />
                                <span>Otrzymasz od nas darmową wycenę i harmonogram.</span>
                            </li>
                        </ul>
                    </div>
                </div>

                {/* --- PRAWA KOLUMNA (FORMULARZ + PROCES MOBILE) --- */}
                <div className="order-2">
                    
                    {/* FORMULARZ */}
                    <div className="bg-white rounded-3xl p-8 lg:p-10 shadow-2xl border border-blue-100 relative overflow-hidden mb-8 lg:mb-0">
                        <form onSubmit={handleSubmit} className="relative z-10 space-y-6">
                            
                            {/* --- POLE PUŁAPKA (HONEYPOT) - UKRYTE DLA UŻYTKOWNIKA --- */}
                            <div className="opacity-0 absolute top-0 left-0 h-0 w-0 z-[-1] overflow-hidden pointer-events-none">
                                <label htmlFor="website_url_check">Website</label>
                                <input
                                    type="text"
                                    id="website_url_check"
                                    name="website_url_check"
                                    tabIndex={-1}
                                    autoComplete="off"
                                    value={honeypot}
                                    onChange={(e) => setHoneypot(e.target.value)}
                                />
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="space-y-2">
                                    <label htmlFor="name" className="text-xs font-bold text-gray-900 uppercase tracking-wide">Imię i Nazwisko *</label>
                                    <input 
                                        type="text" id="name" name="name" required
                                        value={formData.name} onChange={handleChange}
                                        placeholder="Jan Kowalski"
                                        className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label htmlFor="email" className="text-xs font-bold text-gray-900 uppercase tracking-wide">E-mail firmowy *</label>
                                    <input 
                                        type="email" id="email" name="email" required
                                        value={formData.email} onChange={handleChange}
                                        placeholder="jan@firma.pl"
                                        className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
                                    />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <label htmlFor="phone" className="text-xs font-bold text-gray-900 uppercase tracking-wide">Telefon kontaktowy *</label>
                                <input 
                                    type="tel" id="phone" name="phone" required
                                    value={formData.phone} onChange={handleChange}
                                    placeholder="+48 000 000 000"
                                    className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
                                />
                            </div>

                            <div className="space-y-3">
                                <label className="text-xs font-bold text-gray-900 uppercase tracking-wide">Czym jesteś zainteresowany? *</label>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                    {SERVICES.map((s) => (
                                        <button
                                            key={s}
                                            type="button"
                                            onClick={() => setSelection('service', s)}
                                            className={`text-xs font-bold py-3 px-4 rounded-lg border transition text-center ${
                                                formData.service === s 
                                                ? 'bg-black text-white border-black' 
                                                : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'
                                            }`}
                                        >
                                            {s}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div className="space-y-3">
                                <label className="text-xs font-bold text-gray-900 uppercase tracking-wide">Szacowany budżet *</label>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                                    {BUDGETS.map((b) => (
                                        <button
                                            key={b}
                                            type="button"
                                            onClick={() => setSelection('budget', b)}
                                            className={`text-xs font-bold py-3 px-2 rounded-lg border transition text-center ${
                                                formData.budget === b 
                                                ? 'bg-blue-600 text-white border-blue-600' 
                                                : 'bg-white text-gray-600 border-gray-200 hover:border-blue-300'
                                            }`}
                                        >
                                            {b}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div className="space-y-2">
                                <label htmlFor="message" className="text-xs font-bold text-gray-900 uppercase tracking-wide">Szczegóły projektu</label>
                                <textarea 
                                    id="message" name="message" rows={3}
                                    value={formData.message} onChange={handleChange}
                                    placeholder="Opisz krótko na czym Ci zależy..."
                                    className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 transition resize-none"
                                ></textarea>
                            </div>

                            <button 
                                type="submit" 
                                disabled={status === "submitting" || status === "success"}
                                className={`w-full font-bold py-4 rounded-xl transition shadow-lg flex items-center justify-center gap-2 disabled:opacity-80 disabled:cursor-not-allowed ${
                                    status === "success" 
                                    ? "bg-green-600 text-white hover:bg-green-700" 
                                    : "bg-black text-white hover:bg-gray-800"
                                }`}
                            >
                                {status === "submitting" ? (
                                    <>
                                        <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                                        Wysyłanie...
                                    </>
                                ) : status === "success" ? (
                                    <>
                                        <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3"><path d="M5 13l4 4L19 7"/></svg>
                                        Wysłano pomyślnie!
                                    </>
                                ) : (
                                    "Wyślij zapytanie"
                                )}
                            </button>
                            
                            <p className="text-center text-xs text-gray-400 mt-4">
                                Klikając przycisk, akceptujesz naszą politykę prywatności.
                            </p>
                        </form>
                    </div>

                    {/* --- PROCES (MOBILE ONLY) --- */}
                    <div className="bg-white rounded-2xl p-8 border border-gray-200 shadow-sm block lg:hidden">
                        <h3 className="font-bold text-gray-900 mb-6">Co stanie się po wysłaniu formularza?</h3>
                        <ul className="space-y-4">
                            <li className="flex gap-3 text-sm text-gray-600">
                                <CheckIcon />
                                <span>Przeanalizujemy Twoje zgłoszenie w ciągu <strong>24 godzin</strong>.</span>
                            </li>
                            <li className="flex gap-3 text-sm text-gray-600">
                                <CheckIcon />
                                <span>Przygotujemy wstępny plan działania lub umówimy krótką rozmowę (Discovery Call).</span>
                            </li>
                            <li className="flex gap-3 text-sm text-gray-600">
                                <CheckIcon />
                                <span>Otrzymasz od nas darmową wycenę i harmonogram.</span>
                            </li>
                        </ul>
                    </div>

                </div>

            </div>
        </div>
      </section>

      {/* --- FAQ SECTION --- */}
      <section className="py-20 border-t border-gray-200 bg-white">
          <div className="mx-auto max-w-4xl px-6 text-center">
              <h2 className="text-2xl font-bold text-gray-900 mb-10">Masz wątpliwości?</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-left">
                  <div className="bg-gray-50 p-6 rounded-xl">
                      <h3 className="font-bold text-gray-900 mb-2">Czy wycena jest płatna?</h3>
                      <p className="text-gray-600 text-sm">Nie. Wstępna analiza i wycena są całkowicie darmowe i niezobowiązujące.</p>
                  </div>
                  <div className="bg-gray-50 p-6 rounded-xl">
                      <h3 className="font-bold text-gray-900 mb-2">Jak szybko startujemy?</h3>
                      <p className="text-gray-600 text-sm">Zazwyczaj możemy rozpocząć prace w ciągu 3-5 dni roboczych od podpisania umowy.</p>
                  </div>
                  <div className="bg-gray-50 p-6 rounded-xl">
                      <h3 className="font-bold text-gray-900 mb-2">Jak szybko odpowiadamy?</h3>
                      <p className="text-gray-600 text-sm">Staramy się odpowiadać na każde zgłoszenie maksymalnie w ciągu 24 godzin (w dni robocze).</p>
                  </div>
                  <div className="bg-gray-50 p-6 rounded-xl">
                      <h3 className="font-bold text-gray-900 mb-2">Co ze wsparciem po wdrożeniu?</h3>
                      <p className="text-gray-600 text-sm">Nie zostawiamy Cię samego. Oferujemy 12-miesięczną gwarancję na kod oraz pakiety opieki technicznej.</p>
                  </div>
              </div>
          </div>
      </section>

    </main>
  );
}